import { useId } from 'react'
import type { Testimonial } from '@/content/testimonials'
import { site } from '@/content/site'
import { StarRating } from '@/components/reviews/StarRating'
import { cn } from '@/lib/utils'

type Props = {
  testimonial: Testimonial
  className?: string
}

/** Soft floral sprigs (reference-style), purely decorative */
function FloralSprig({ className }: { className?: string }) {
  return (
    <svg
      className={cn('pointer-events-none absolute text-white', className)}
      viewBox="0 0 100 180"
      fill="none"
      aria-hidden
    >
      <path
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        opacity="0.9"
        d="M50 178c-8-40-6-78 4-112M38 120c12-4 22-2 28 6M44 88c-10-6-18-4-24 4M58 96c10-8 20-6 26 2M52 52c-14 2-22 10-20 22M48 36c16 0 24-8 22-20"
      />
      <circle cx="34" cy="118" r="2.2" fill="currentColor" opacity="0.85" />
      <circle cx="62" cy="102" r="2" fill="currentColor" opacity="0.8" />
      <circle cx="48" cy="78" r="1.8" fill="currentColor" opacity="0.75" />
      <circle cx="72" cy="70" r="1.6" fill="currentColor" opacity="0.7" />
      <circle cx="40" cy="54" r="1.5" fill="currentColor" opacity="0.72" />
      <circle cx="58" cy="38" r="1.4" fill="currentColor" opacity="0.68" />
      <circle cx="46" cy="26" r="1.2" fill="currentColor" opacity="0.65" />
    </svg>
  )
}

/**
 * Scrapbook / collage-style guest card: paper field, torn-edge color panel,
 * centered square portrait, quote on the panel, delicate sprigs.
 */
export function GoogleReviewCard({ testimonial: t, className }: Props) {
  const rating = t.rating ?? 5
  const clipId = useId().replace(/:/g, '')

  return (
    <article
      className={cn(
        'relative flex min-h-[26rem] flex-col overflow-hidden rounded-[1.75rem] shadow-[var(--shadow-card)] ring-1 ring-hairline',
        'transition-[transform,box-shadow] duration-300 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-0.5 hover:shadow-[var(--shadow-card-hover)] motion-reduce:transform-none motion-reduce:hover:translate-y-0 motion-reduce:hover:shadow-[var(--shadow-card)] sm:min-h-[28rem]',
        className
      )}
    >
      <svg className="pointer-events-none absolute h-0 w-0" aria-hidden>
        <defs>
          <clipPath id={clipId} clipPathUnits="objectBoundingBox">
            <path d="M0,1 L0,0.38 C0.04,0.34 0.08,0.4 0.12,0.35 C0.18,0.4 0.22,0.33 0.28,0.38 C0.34,0.32 0.4,0.37 0.46,0.33 C0.52,0.38 0.58,0.31 0.64,0.36 C0.7,0.3 0.76,0.35 0.82,0.32 C0.88,0.36 0.94,0.31 1,0.35 L1,1 Z" />
          </clipPath>
        </defs>
      </svg>

      {/* Paper base */}
      <div className="absolute inset-0 bg-canvas" />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.35] mix-blend-multiply"
        style={{
          backgroundImage: `repeating-linear-gradient(
            -12deg,
            transparent,
            transparent 2px,
            rgba(0,0,0,0.02) 2px,
            rgba(0,0,0,0.02) 3px
          )`,
        }}
        aria-hidden
      />

      <FloralSprig className="left-0 top-16 z-[1] w-[5.5rem] -translate-x-1 sm:top-20 sm:w-28" />
      <FloralSprig className="right-0 top-[4.5rem] z-[1] w-[6rem] translate-x-1 scale-x-[-1] sm:top-24 sm:w-32" />

      <p className="relative z-20 px-4 pt-9 text-center font-display text-[1.65rem] italic leading-tight tracking-tight text-primary sm:pt-10 sm:text-[1.85rem]">
        Thank you
      </p>

      <div className="relative z-30 mx-auto mt-5 flex justify-center sm:mt-6">
        <div className="size-[7.25rem] shrink-0 rounded-lg bg-white p-1.5 shadow-lg ring-[5px] ring-white sm:size-[7.75rem] sm:p-2 sm:ring-[6px]">
          <img
            src={t.photoSrc}
            alt=""
            width={320}
            height={320}
            loading="lazy"
            decoding="async"
            referrerPolicy="no-referrer"
            className="h-full w-full rounded-md object-cover"
          />
        </div>
      </div>

      <div
        className="relative z-20 mx-[5%] mt-[-3.5rem] flex min-h-0 flex-1 flex-col items-center bg-primary px-5 pb-8 pt-[4.75rem] text-center sm:mx-[6%] sm:px-8 sm:pb-9 sm:pt-[5rem]"
        style={{ clipPath: `url(#${clipId})` }}
      >
        <StarRating rating={rating} variant="onDark" className="justify-center" />
        <blockquote className="mt-5 max-w-[20rem] border-0 p-0">
          <p className="font-display text-[1.05rem] leading-relaxed text-on-primary [text-shadow:0_1px_2px_rgba(0,0,0,0.1)] sm:text-[1.125rem]">
            &ldquo;{t.quote}&rdquo;
          </p>
        </blockquote>
        <cite className="mt-4 block font-display text-base text-on-primary/90 sm:text-[1.05rem]">
          <span className="not-italic">&mdash; </span>
          <span className="italic">{t.name}</span>
        </cite>
        {t.relativeTime ? (
          t.isoDate ? (
            <time
              className="mt-2 text-[11px] font-medium uppercase tracking-[0.16em] text-on-primary/70"
              dateTime={t.isoDate}
            >
              {t.relativeTime}
            </time>
          ) : (
            <span className="mt-2 text-[11px] font-medium uppercase tracking-[0.16em] text-on-primary/70">
              {t.relativeTime}
            </span>
          )
        ) : null}
      </div>

      <p className="relative z-20 px-4 pb-6 pt-3 text-center font-display text-[11px] font-medium tracking-[0.12em] text-muted sm:text-xs">
        {site.name} · {site.city}
      </p>
    </article>
  )
}
